<script lang="ts">
import type { NullableError } from '@/types'
import { ValidationException } from '@/validation-exception'
import { Icon } from '@goodlifeplus/component-library'
import { type PropType, defineComponent, useTemplateRef } from 'vue'

/**
 * A general component with common input functionality to extend more specific input types from.
 *
 * @see https://www.w3.org/WAI/tutorials/forms/
 * @see https://www.w3.org/TR/UNDERSTANDING-WCAG20/minimize-error-identified.html
 * @see https://www.w3.org/TR/2016/NOTE-WCAG20-TECHS-20161007/G89.html
 * @see https://www.w3.org/TR/2016/NOTE-WCAG20-TECHS-20161007/SCR32
 * @see https://www.w3.org/TR/2016/NOTE-WCAG20-TECHS-20161007/ARIA19.html
 * @see https://www.w3.org/TR/WCAG20-TECHS/ARIA18 On severe errors displays with a alertdialog
 * @see https://www.w3.org/TR/WCAG20-TECHS/ARIA21
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/alert/examples/alert/
 */
export default defineComponent({
  components: { Icon },
  inheritAttrs: false,
  props: {
    /**
     * Text describing the reason for the input.
     */
    label: {
      type: String,
    },
    /**
     * Adds a show/hide toggle for password inputs (controls the input `type`).
     */
    revealable: {
      type: Boolean,
      default: false,
    },
    /**
     * Used as a reactive floating text when the input has focus.
     */
    useFloatingPlaceholder: {
      type: Boolean,
      default: true,
    },
    /**
     * The name of the input sent to the API endpoint.
     */
    name: {
      type: String,
      required: true,
    },
    /**
     * Text used when the input is empty.
     */
    placeholder: {
      type: String,
    },
    /**
     * Text used by browsers to autofill the input.
     */
    autocomplete: {
      type: String,
    },
    /**
     * Text displayed below the input element.
     */
    hint: {
      type: String,
    },
    /**
     * Moves the label to the left-hand side of the input.
     */
    inline: {
      type: Boolean,
    },
    /**
     * @model
     */
    modelValue: {
      type: [Number, String, null],
      required: true,
    },
    /**
     * Displays input validation errors returned.
     */
    errors: {
      type: Object as PropType<NullableError>,
    },
    /**
     * Adds classes to the container of the input field.
     */
    containerClass: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  setup() {
    return {
      input: useTemplateRef<HTMLInputElement>('input'),
    }
  },
  data() {
    return {
      revealed: false,
    }
  },
  computed: {
    // When revealable, we own the input `type` (password ↔ text); otherwise the
    // type passes through from `$attrs`.
    inputType(): string | undefined {
      if (this.revealable) {
        return this.revealed ? 'text' : 'password'
      }
      return this.$attrs.type as string | undefined
    },
    value: {
      get() {
        return this.modelValue
      },
      set(value: number | string) {
        this.$emit('update:modelValue', value)
      },
    },
    required(): boolean {
      return (this.$attrs.required ?? false) as boolean
    },
    disabled(): boolean {
      return (this.$attrs.disabled ?? false) as boolean
    },
    hasErrors(): boolean {
      return this.inputErrors.length > 0
    },
    inputErrors() {
      return this.errors instanceof ValidationException
        ? this.errors.get(this.name)
        : []
    },
    id(): string {
      return this.$attrs.id?.toString() ?? this.name
    },
  },
  methods: {
    /**
     * Handle blur events to trim whitespace from input value
     */
    handleBlur() {
      if (typeof this.value === 'string') {
        const trimmedValue = this.value.trim()
        if (trimmedValue !== this.value) {
          this.value = trimmedValue
        }
      }
    },
  },
})
</script>

<template>
  <div
    :class="[
      disabled ? 'opacity-50' : '',
      inline ? 'flex-row items-center' : 'flex-col',
      `${name}-input-container`,
      containerClass,
    ]"
    class="relative mb-3 flex flex-grow gap-2"
  >
    <template v-if="label && !useFloatingPlaceholder">
      <label class="block text-sm font-bold" :for="name">
        {{ label }}
        <span v-if="required" class="text-xs">*</span>
      </label>
    </template>

    <input
      :id="id"
      v-bind="$attrs"
      ref="input"
      v-model="value"
      :type="inputType"
      :aria-errormessage="name + '-errors'"
      :aria-describedBy="name + '-hint'"
      :aria-invalid="hasErrors"
      :autocomplete="autocomplete || name"
      :class="[
        hasErrors ? 'border-red-500 !pr-12' : 'focus:border-transparent',
        revealable && !hasErrors ? '!pr-12' : '',
        disabled ? 'hover:cursor-not-allowed' : '',
        useFloatingPlaceholder ? 'placeholder-transparent' : '!pt-3',
      ]"
      :name
      :placeholder
      class="form-element peer form-input text-black"
      @blur="handleBlur"
    />
    <AppIcon
      v-if="hasErrors"
      :icon="Icons.Error"
      class="pointer-events-none absolute right-4 text-red-500"
      :class="useFloatingPlaceholder ? 'top-4' : 'top-10'"
    />
    <button
      v-if="revealable && !hasErrors"
      type="button"
      :aria-label="revealed ? 'Hide password' : 'Show password'"
      class="absolute right-4 -translate-y-1/2 text-slate-500 hover:text-slate-700"
      :class="useFloatingPlaceholder ? 'top-7' : 'top-[53px]'"
      @click="revealed = !revealed"
    >
      <Icon
        :name="revealed ? UI.Icon.Name.EyeDefault : UI.Icon.Name.EyeCrossed"
      />
    </button>

    <template v-if="placeholder && useFloatingPlaceholder">
      <label :for="name" class="floating-label">
        {{ placeholder }}
        <span v-if="required" class="text-xs text-[#5F7D92]"> * </span>
      </label>
    </template>

    <div
      v-if="hasErrors"
      :id="id + '-errors'"
      :aria-live="hasErrors ? 'assertive' : 'off'"
      aria-atomic="true"
      class="errors"
    >
      <div
        v-for="error in inputErrors"
        :key="error"
        class="text-xs font-bold text-red-500"
        v-html="error"
      />
    </div>

    <div v-if="hint && !hasErrors" :id="id + '-hint'" class="text-xs">
      {{ hint }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.floating-label {
  @apply pointer-events-none absolute left-4 top-1 flex w-max items-center gap-1 text-xs text-[#5F7D92] transition-all peer-placeholder-shown:top-4.5 peer-placeholder-shown:text-base peer-focus:top-1 peer-focus:text-xs;
}

.required {
  @apply text-[#5F7D92];
}

.bg-black .required {
  @apply text-pure-white;
}
</style>
